import React, { useCallback } from 'react';
import { SupplierThread, ThreadMessage } from '../components/forms/outreach/types';
import { clearOutreachNote } from '../utils/outreachNotes';
import { invalidateOrgCredits } from './useOrgCredits';
import { useOutreachSend } from './useOutreachSend';

export type ThreadSendResult = 'sent' | 'failed' | 'rejected';

type MessageMap = Record<string, ThreadMessage[]>;

interface ThreadSendOptions {
  rfqId?: string;
  bomId: string | null;
  setThreadMessages: React.Dispatch<React.SetStateAction<MessageMap>>;
  /** A first send re-keys the thread; the caller's URL follows the new id. */
  onThreadRekeyed: (realThreadId: string) => void;
  refreshThreads: () => void;
}

const patchMessage = (list: ThreadMessage[] | undefined, id: string, patch: Partial<ThreadMessage>) =>
  (list || []).map((m) => (m.id === id ? { ...m, ...patch } : m));

/**
 * The whole send, as the thread shows it: the query appears at once as the
 * buyer's own bubble marked pending, becomes a sent message when the email has
 * left, or stays in place marked "Not sent" with a retry — the buyer's words
 * are never silently taken back.
 */
export const useOutreachThreadSend = ({
  rfqId,
  bomId,
  setThreadMessages,
  onThreadRekeyed,
  refreshThreads,
}: ThreadSendOptions) => {
  const sendFlow = useOutreachSend(rfqId);
  const { sendQuery } = sendFlow;

  const deliver = useCallback(
    async (thread: SupplierThread, messageId: string, query: string, isNegotiation: boolean): Promise<ThreadSendResult> => {
      const threadId = thread.id;
      const outcome = await sendQuery(thread, query, isNegotiation);
      if (outcome.rejected) {
        setThreadMessages((prev) => ({
          ...prev,
          [threadId]: (prev[threadId] || []).filter((m) => m.id !== messageId),
        }));
        return 'rejected';
      }
      if (!outcome.ok) {
        setThreadMessages((prev) => ({
          ...prev,
          [threadId]: patchMessage(prev[threadId], messageId, { sendState: 'failed', sendFailure: outcome.failure }),
        }));
        return 'failed';
      }

      // The queued discovery note (if any) is now part of a sent message.
      if (bomId && thread.componentId) clearOutreachNote(bomId, thread.componentId, thread.supplierName);
      const realThreadId = outcome.threadId;
      setThreadMessages((prev) => {
        const finalized = patchMessage(prev[threadId], messageId, {
          sendState: undefined,
          sendFailure: undefined,
          timestamp: 'Just now',
        });
        if (realThreadId && realThreadId !== threadId) {
          // First send on a draft thread: the refreshed BOM will re-key this
          // thread under the real comms id — move the messages along with it.
          const rest = { ...prev };
          delete rest[threadId];
          return { ...rest, [realThreadId]: [...(prev[realThreadId] || []), ...finalized] };
        }
        return { ...prev, [threadId]: finalized };
      });
      if (realThreadId && realThreadId !== threadId) onThreadRekeyed(realThreadId);
      invalidateOrgCredits(); // an OUTREACH_MESSAGE charge just settled
      refreshThreads();
      return 'sent';
    },
    [sendQuery, bomId, setThreadMessages, onThreadRekeyed, refreshThreads],
  );

  const sendAskToThread = useCallback(
    async (thread: SupplierThread, ask: string, isNegotiation: boolean): Promise<ThreadSendResult> => {
      const query = ask.trim();
      if (!query) return 'rejected';
      const pending: ThreadMessage = {
        id: `msg-${Date.now()}`,
        sender: 'buyer',
        senderName: 'You',
        timestamp: '',
        text: query,
        sourceQuery: query,
        sendState: 'sending',
      };
      setThreadMessages((prev) => ({
        ...prev,
        [thread.id]: [...(prev[thread.id] || thread.messages), pending],
      }));
      return deliver(thread, pending.id, query, isNegotiation);
    },
    [deliver, setThreadMessages],
  );

  /** Sends a "Not sent" bubble again, in place. */
  const retrySend = useCallback(
    async (thread: SupplierThread, message: ThreadMessage): Promise<ThreadSendResult> => {
      const query = (message.sourceQuery || message.text).trim();
      setThreadMessages((prev) => ({
        ...prev,
        [thread.id]: patchMessage(prev[thread.id] || thread.messages, message.id, {
          sendState: 'sending',
          sendFailure: undefined,
        }),
      }));
      return deliver(thread, message.id, query, false);
    },
    [deliver, setThreadMessages],
  );

  return {
    sendAskToThread,
    retrySend,
    isSendingThread: sendFlow.isSendingThread,
    sendError: sendFlow.sendError,
    dismissSendError: sendFlow.dismissSendError,
  };
};
